import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [username, setUsername] = useState('admin');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Les contrasenyes no coincideixen');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La contrasenya ha de tenir almenys 6 caràcters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/admin/reset-password`, {
        username,
        reset_code: resetCode,
        new_password: newPassword
      });

      toast.success('Contrasenya restablerta correctament');
      
      setTimeout(() => {
        navigate('/admin/login');
      }, 1500);
    } catch (error) {
      console.error('Error restablint contrasenya:', error);
      toast.error(error.response?.data?.detail || 'Error restablint la contrasenya');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center px-6 py-12 bg-muted/30">
      <div className="w-full max-w-md">
        <Link 
          to="/admin/login" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Tornar al login
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-card rounded-2xl shadow-sm border border-border/50 p-8"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center">
              <KeyRound className="h-8 w-8" />
            </div>
          </div>

          <h1 className="font-heading text-3xl font-medium text-center text-foreground mb-2">
            Restablir Contrasenya
          </h1>
          <p className="text-center text-muted-foreground mb-8">
            Introdueix el codi de 6 dígits que has rebut per email
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-base mb-2">Usuari</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="reset-username"
                className="h-12 rounded-xl"
                required
                disabled
              />
            </div>

            <div>
              <Label htmlFor="code" className="text-base mb-2">Codi de Restabliment (6 dígits)</Label>
              <Input
                id="code"
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                data-testid="reset-code"
                className="h-12 rounded-xl"
                placeholder="123456"
                maxLength={6}
                required
              />
            </div>

            <div>
              <Label htmlFor="new" className="text-base mb-2">Contrasenya Nova</Label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-new-password"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div>
              <Label htmlFor="confirm" className="text-base mb-2">Confirmar Contrasenya</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="reset-confirm-password"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-password-button"
              className="w-full h-12 text-base font-semibold rounded-xl"
            >
              {loading ? 'Restablint...' : 'Restablir Contrasenya'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/admin/request-reset" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              No has rebut el codi? Sol·licita'n un de nou
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;